import React, { useState, useEffect } from "react";
import DonutChart from "./DonutChart";
import {
  getAllDevices,
  fetchDeviceHealth,
  DeviceHealth,
  getCbomKeys,
  getCbomCertificates,
  fetchKeys,
  fetchCertificates,
} from "services/apiServices";
import { CbomKeys, Device, CbomCertificate } from "@schemas";
import { useProjectContext } from "context/ProjectContext";

const DeviceHealthSummary: React.FC = () => {
  const [allKeys, setAllKeys] = useState<CbomKeys[]>([]);
  const [allCertificates, setAllCertificates] = useState<CbomCertificate[]>([]);
  const { state } = useProjectContext();
  const [vulnerableDevicesCount, setVulnerableDevicesCount] =
    useState<number>(0);
  const [deviceData, setDeviceData] = useState([
    {
      description: "Total No. of Devices",
      value: 0,
    },
    {
      description: "No. of Devices not active",
      value: 0,
      percent: "",
    },
    {
      description: "No. of Devices with scans not active",
      value: 0,
      percent: "",
    },
    {
      description: "No. of Vulnerable Devices",
      value: vulnerableDevicesCount,
      percent: "",
    },
  ]);
  const [noOfDevicesData, setNOOfDevicesData] = useState([
    { name: "Active", noOfItems: 0, color: "custom.norisk", value: "" },
    {
      name: "Inactive",
      noOfItems: 0,
      color: "custom.riskveryhigh",
      value: "",
    },
  ]);
  const [noOfDevicesScansData, setNoOfDevicesScansData] = useState([
    { name: "Scanned", noOfItems: 0, color: "custom.norisk", value: "" },

    {
      name: "Not Scanned (in past 7 days)",
      noOfItems: 0,
      color: "custom.riskveryhigh",
      value: "",
    },
  ]);

  const [noOfVulnerableDevices, setNoOfVulnerableDevices] = useState([
    { name: "Unknown", noOfItems: 0, color: "custom.riskverylow", value: "" },
    { name: "Safe", noOfItems: 0, color: "custom.norisk", value: "" },
    {
      name: "Vulnerable",
      noOfItems: 0,
      color: "custom.riskveryhigh",
      value: "",
    },
  ]);

  const formatPercent = (partial: number, total: number) =>
    total > 0 ? `(${((partial / total) * 100).toFixed(0)}%)` : "";

  useEffect(() => {
    const fetchAllData = async () => {
      if (!state.currentProjectID) return;

      await Promise.all([fetchAllDevices(), fetchKeysAndCertificates()]);
    };

    fetchAllData();
  }, []);

  const fetchKeysAndCertificates = async () => {
    if (state.currentProjectID) {
      await fetchKeys(
        (data) => {
          setAllKeys(data);
        },
        "",
        false,
        state.currentProjectID
      );
      await fetchCertificates(
        (data) => {
          setAllCertificates(data);
        },
        "",
        false,
        state.currentProjectID
      );
    }
  };

  const fetchAllDevices = async () => {
    try {
      if (state.currentProjectID) {
        const [deviceResponse, deviceHealth] = await Promise.all([
          getAllDevices(state.currentProjectID),
          fetchDeviceHealth(state.currentProjectID),
        ]);
        const allDevices = deviceResponse.data;

        const activeDevices = allDevices.filter(
          (device: Device) => device.status?.toLowerCase() === "active"
        ).length;

        const inactiveDevices = allDevices.filter(
          (device: Device) => device.status?.toLowerCase() === "inactive"
        ).length;

        const activeScans = deviceHealth.filter(
          (device: DeviceHealth) => device.last_cbom_sync_time !== null
        );

        const scansNotActive = deviceHealth.filter(
          (device: DeviceHealth) => device.last_cbom_sync_time === null
        );

        setNOOfDevicesData([
          {
            name: "Active",
            noOfItems: activeDevices,
            color: "custom.norisk",
            value: "",
          },
          {
            name: "Inactive",
            noOfItems: inactiveDevices,
            color: "custom.riskveryhigh",
            value: "",
          },
        ]);
        setDeviceData([
          {
            description: "Total No. of Devices",
            value: allDevices.length,
          },
          {
            description: "No. of Devices not active",
            value: inactiveDevices,
            percent: formatPercent(inactiveDevices, allDevices.length),
          },
          {
            description: "No. of Devices with scans not active",
            value: scansNotActive.length,
            percent: formatPercent(scansNotActive.length, allDevices.length),
          },
          {
            description: "No. of Vulnerable Devices",
            value: vulnerableDevicesCount,
            percent: formatPercent(vulnerableDevicesCount, allDevices.length),
          },
        ]);
        setNoOfDevicesScansData([
          {
            name: "Scanned",
            noOfItems: activeScans.length,
            color: "custom.norisk",
            value: "",
          },

          {
            name: "Not Scanned (in past 7 days)",
            noOfItems: scansNotActive.length,
            color: "custom.riskveryhigh",
            value: "",
          },
        ]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const processAllKeysAndCertificates = async () => {
    try {
      const deviceStatusMap = new Map<
        string,
        "Vulnerable" | "Unknown" | "Safe"
      >();

      //  Mark vulnerable from keys
      allKeys.forEach((key: any) => {
        if (key.quantum_safe_status === "Vulnerable") {
          deviceStatusMap.set(key.device_id, "Vulnerable");
        }
      });

      //  Mark vulnerable from certificates
      allCertificates.forEach((cert: any) => {
        if (
          cert.signature_algorithm_qv === "Vulnerable" ||
          cert.public_key_algorithm_qv === "Vulnerable"
        ) {
          deviceStatusMap.set(cert.device_id, "Vulnerable");
        }
      });

      //  Mark unknowns only if not already vulnerable
      allKeys.forEach((key: any) => {
        if (
          key.quantum_safe_status === "Unknown" &&
          !deviceStatusMap.has(key.device_id)
        ) {
          deviceStatusMap.set(key.device_id, "Unknown");
        }
      });

      allCertificates.forEach((cert: any) => {
        if (
          (cert.signature_algorithm_qv === "Unknown" ||
            cert.public_key_algorithm_qv === "Unknown") &&
          !deviceStatusMap.has(cert.device_id)
        ) {
          deviceStatusMap.set(cert.device_id, "Unknown");
        }
      });

      // Mark safe if not present in the map at all
      const allDeviceIds = new Set<string>([
        ...allKeys.map((k: any) => k.device_id),
        ...allCertificates.map((c: any) => c.device_id),
      ]);

      console.log(allDeviceIds);

      const counts = {
        vulnerable: 0,
        unknown: 0,
        safe: 0,
      };

      allDeviceIds.forEach((deviceId) => {
        const status = deviceStatusMap.get(deviceId);
        if (status === "Vulnerable") counts.vulnerable++;
        else if (status === "Unknown") counts.unknown++;
        else counts.safe++;
      });

      setVulnerableDevicesCount(counts.vulnerable);
      setNoOfVulnerableDevices([
        {
          name: "Unknown",
          noOfItems: counts.unknown,
          color: "custom.riskverylow",
          value: "",
        },
        {
          name: "Safe",
          noOfItems: counts.safe,
          color: "custom.norisk",
          value: "",
        },
        {
          name: "Vulnerable",
          noOfItems: counts.vulnerable,
          color: "custom.riskveryhigh",
          value: "",
        },
      ]);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (allKeys.length && allCertificates.length) {
      processAllKeysAndCertificates();
    }
  }, [allKeys, allCertificates]);

  useEffect(() => {
    setDeviceData((prev) =>
      prev.map((item) =>
        item.description === "No. of Vulnerable Devices"
          ? {
              ...item,
              value: vulnerableDevicesCount,
              percent: formatPercent(vulnerableDevicesCount, prev[0]?.value),
            }
          : item
      )
    );
  }, [vulnerableDevicesCount]);

  return (
    <div className="bg-custom-blue8 rounded-lg px-[10px] py-[20px]">
      <div className="bg-white py-5 rounded-lg shadow-md space-y-5 mb-4">
        {deviceData.map((item, index) => (
          <div key={index} className="flex justify-between items-center px-14">
            <p className="w-1/2 text-custom-blue6 text-sm font-normal">
              {item.description}
            </p>
            <p className="w-1/4 text-custom-blue6 text-sm font-semibold">
              {item.value + ` ${item.percent ? item.percent : ""}`}
            </p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[repeat(auto-fit,minmax(400px,1fr))] gap-4 w-full">
        <div>
          <DonutChart
            absoluteYearsData={noOfDevicesData}
            title="No. of Devices"
            footer=""
          />
        </div>
        <div>
          <DonutChart
            absoluteYearsData={noOfDevicesScansData}
            title="No. of Devices with scans not active"
            footer=""
          />
        </div>
        <div>
          <DonutChart
            absoluteYearsData={noOfVulnerableDevices}
            title="Vulnerable Devices"
            footer=""
          />
        </div>
      </div>
    </div>
  );
};

export default DeviceHealthSummary;
